import React, {useState} from 'react';
import styled from 'styled-components';
import {CarSelectionSection} from './components/car-selection-section';
import {InputSection} from './components/input-section';
import {ChargingData, OutputSection} from './components/output-section';
import {
  ConfigureButtonWrapper,
  FaqListContextProvider,
  FaqSection,
  getConfigureButtonConfiguration,
  getDisclaimerReferenceProps,
  GridAreaConfigure,
  GridAreaDisclaimers,
  GridAreaInputs,
  GridAreaOutputs,
  Header,
  resultBarInViewProps,
  TrackingEvent,
  useComponent,
  useDevConfig,
  useSharedEmbeddedFeatureAppData,
  useTrackingContextSetter,
  useTrackingFunctions,
  YesNoValues,
  SimulatorLayout,
  SimulatorHeader,
  useGetAppDataContentFlag,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {useChargeTimeCalculationForm} from './use-charge-calculation-form';
import {useTranslation} from './use-translation';
import {
  ChargerTypeValues,
  ChargeTimeTrimModel,
  ChargingOption,
  ChargingOptionV2,
  SimulatorFeatureAppName,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {useTenantConfiguration} from './use-tenant-configuration';
import {ChargeTimeSimulatorTrackingCtx} from './chargetime-tracking';
import {useInView} from 'react-intersection-observer';
import {useTrims} from './use-trims';

export function App(): JSX.Element {
  const tenantConfiguration = useTenantConfiguration();

  const tracking = useTrackingFunctions();
  const {usePreHeatedData} = useDevConfig();
  const isContentLayer = useGetAppDataContentFlag('isContentLayer');

  const setTrackingData =
    useTrackingContextSetter<ChargeTimeSimulatorTrackingCtx>();
  const translations = useTranslation();
  const {selectedModel, selectedTrim, formFields, formState} =
    useChargeTimeCalculationForm();

  const [disablePreHeatedToggle, setDisablePreHeatedToggle] = useState(true);
  const [preHeatedAsDefaultDisabledValue, setPreHeatedAsDefaultDisabledValue] =
    useState(true);

  React.useEffect(() => {
    tracking[TrackingEvent.FeatureAppLoaded]?.({});
  }, [tracking]);

  React.useEffect(() => {
    if (disablePreHeatedToggle) {
      formFields.preHeatedBattery.onChange(
        preHeatedAsDefaultDisabledValue ? YesNoValues.yes : YesNoValues.no
      );
    }
  }, [disablePreHeatedToggle, preHeatedAsDefaultDisabledValue]);

  useSharedEmbeddedFeatureAppData<ChargeTimeTrimModel>(
    useTrims(),
    formFields.modelTypeId.onChange
  );

  const resultDisclaimer = getDisclaimerReferenceProps(
    translations.resultSection.disclaimerText,
    translations.global.disclaimerReferenceLabel,
    translations.global.educationalInfoLayer.closeButtonLabel
  );
  const chargeTimeData = selectedModel.simulationConstants;

  const translateChargingOption = React.useCallback(
    (
      [speed, duration]: ChargingOption,
      selectedChargerType: ChargerTypeValues
    ): ChargingData => ({
      label: translations.resultSection.resultsLabel.replace(
        '%%chargespeed%%',
        speed.toString()
      ),
      ariaLabel: translations.resultSection.resultsAriaLabel.replace(
        '%%chargespeed%%',
        speed.toString()
      ),
      chargeUpMinutes: duration,
      selectedChargerType,
    }),
    [translations]
  );

  const translateChargingOptionV2 = React.useCallback(
    (
      chargingOptions: ChargingOptionV2,
      selectedChargerType: ChargerTypeValues,
      usePreHeatedValue: boolean
    ): ChargingData => ({
      label: translations.resultSection.ariaLabel.replace(
        '%%chargespeed%%',
        chargingOptions.chargingSpeed.toString()
      ),
      ariaLabel: translations.resultSection.ariaLabel.replace(
        '%%chargespeed%%',
        chargingOptions.chargingSpeed.toString()
      ),
      // TODO in the third task check if this logic is enough to cover all scenarios
      chargeUpMinutes:
        (usePreHeatedValue
          ? chargingOptions.duration.preHeated
          : chargingOptions.duration.nonPreHeated) ??
        (chargingOptions.duration.preHeated ||
          chargingOptions.duration.nonPreHeated ||
          0),
      selectedChargerType,
    }),
    [translations]
  );

  const chargeTimeResult = React.useMemo(() => {
    const scenario = formState.chargingLocation;
    const outsideTemperature = formState.outsideTemperature;
    const startSoc = formState.chargeDelta[0];
    const finalSoc = formState.chargeDelta[1];
    const selectedChargerType = formFields.chargerType.value;

    const entry =
      chargeTimeData.scenarios[scenario]?.outsideTemperature[outsideTemperature]
        ?.startSoc[startSoc]?.finalSoc[finalSoc];

    let newEntry: ChargingOption = [0, 0];

    if (entry) {
      const chargerTypeData = entry[selectedChargerType] as ChargingOption[];

      if (chargerTypeData && chargerTypeData?.length > 0) {
        const chargingData = chargerTypeData.find(
          (data) => data[0] === Number(formFields.chargingPower.value)
        );
        newEntry = chargingData as ChargingOption;
      } else {
        newEntry = chargerTypeData as unknown as ChargingOption;
      }
    }

    if (!entry) {
      throw new Error(
        `Simulation constants missing for ${JSON.stringify({
          scenario,
          outsideTemperature,
          startSoc,
          finalSoc,
        })}`
      );
    }

    return translateChargingOption(newEntry ?? [0, 0], selectedChargerType);
  }, [formState, translateChargingOption]);

  const getCurrentChargingOptions = React.useMemo(() => {
    const scenario = formState.chargingLocation;
    const outsideTemperature = formState.outsideTemperature;
    const startSoc = formState.chargeDelta[0];
    const finalSoc = formState.chargeDelta[1];
    const selectedChargerType = formFields.chargerType.value;

    const entry =
      chargeTimeData.scenarios[scenario]?.outsideTemperature[outsideTemperature]
        ?.startSoc[startSoc]?.finalSoc[finalSoc];

    let newEntry: ChargingOptionV2 = {
      chargingSpeed: 0,
      duration: {
        preHeated: undefined,
        nonPreHeated: undefined,
      },
    };

    if (entry) {
      const chargerTypeData = entry[selectedChargerType] as ChargingOptionV2[];

      if (chargerTypeData && chargerTypeData?.length > 0) {
        const chargingData = chargerTypeData.find(
          (data) =>
            data.chargingSpeed === Number(formFields.chargingPower.value)
        );
        newEntry = chargingData as ChargingOptionV2;
      } else {
        newEntry = chargerTypeData as unknown as ChargingOptionV2;
      }
    }
    if (!entry) {
      throw new Error(
        `Simulation constants missing for ${JSON.stringify({
          scenario,
          outsideTemperature,
          startSoc,
          finalSoc,
        })}`
      );
    }
    return newEntry;
  }, [formState, translateChargingOption]);

  const chargeTimeResultV2 = React.useMemo(() => {
    const selectedChargerType = formFields.chargerType.value;
    const preHeatedToggle = formFields.preHeatedBattery.value;
    const defaultPreHeated =
      !tenantConfiguration.inputSection.preHeatedBatteryQuestion
        ?.usePreHeatedBatteryByDefault === false;

    const newEntry = getCurrentChargingOptions;
    if (newEntry) {
      setDisablePreHeatedToggle(
        !(newEntry.duration.nonPreHeated && newEntry.duration.preHeated)
      );
      setPreHeatedAsDefaultDisabledValue(
        newEntry.duration.preHeated !== undefined
      );
    }
    return translateChargingOptionV2(
      newEntry ?? {
        chargingSpeed: 0,
        duration: {
          preHeated: undefined,
          nonPreHeated: undefined,
        },
      },
      selectedChargerType,
      !tenantConfiguration.featureFlags?.showPreHeatedDataToggle
        ? defaultPreHeated
        : preHeatedToggle === YesNoValues.yes
    );
  }, [
    formFields.chargerType.value,
    formFields.preHeatedBattery.value,
    getCurrentChargingOptions,
    tenantConfiguration.featureFlags?.showPreHeatedDataToggle,
    tenantConfiguration.inputSection.preHeatedBatteryQuestion
      ?.usePreHeatedBatteryByDefault,
    translateChargingOptionV2,
  ]);

  const Disclaimer = useComponent('Disclaimer');

  React.useMemo(() => {
    // useMemo instead of useEffect to be synchronous and faster than the load
    // event. This is ok for this use-case where we only write data into a ref.
    setTrackingData({
      tenantConfiguration,
      translations,
      selectedModel,
      selectedTrim,
      formState,
    });
  }, [
    setTrackingData,
    tenantConfiguration,
    translations,
    selectedModel,
    selectedTrim,
    formState,
  ]);

  const {ref, inView} = useInView(resultBarInViewProps);

  const configureButtonConfiguration =
    getConfigureButtonConfiguration(selectedTrim);

  return (
    <FaqListContextProvider>
      <AppLayout>
        <SimulatorLayout>
          <SimulatorHeader isContentLayer={isContentLayer}>
            <Header
              heading={translations.header.heading}
              showConfigureButton={
                tenantConfiguration.featureFlags?.showConfigureButton
              }
              configureButtonTranslations={translations.global.configureButton}
              configureButtonConfiguration={configureButtonConfiguration}
              featureApp={SimulatorFeatureAppName.Chargetime}
            />
          </SimulatorHeader>
          <SimulatorContentLayout isContentLayer={isContentLayer}>
            <GridAreaCarSelection>
              <CarSelectionSection
                selectedModel={selectedModel}
                selectedTrim={selectedTrim}
                modelEngineField={formFields.modelEngineId}
                modelTypeField={formFields.modelTypeId}
              />
            </GridAreaCarSelection>
            <GridAreaInputs innerRef={ref}>
              <InputSection
                chargingLocationField={formFields.chargingLocation}
                chargeDeltaField={formFields.chargeDelta}
                outsideTemperatureField={formFields.outsideTemperature}
                chargeStartMax={chargeTimeData.startSocMax}
                chargeFinalMin={chargeTimeData.finalSocMin}
                chargerTypeField={formFields.chargerType}
                chargingPowerField={formFields.chargingPower}
                preHeatedBatteryField={formFields.preHeatedBattery}
                disablePreHeatedToggle={
                  usePreHeatedData ? disablePreHeatedToggle : false
                }
              />
            </GridAreaInputs>
            <GridAreaOutputs>
              <OutputSection
                disclaimer={resultDisclaimer}
                chargeTimeResult={
                  usePreHeatedData ? chargeTimeResultV2 : chargeTimeResult
                }
                inView={inView}
              />
            </GridAreaOutputs>
            <GridAreaConfigure>
              <ConfigureButtonWrapper
                showButton={
                  tenantConfiguration.featureFlags?.showConfigureButton
                }
                configuration={configureButtonConfiguration}
                translations={translations.global.configureButton}
              />
            </GridAreaConfigure>
            {resultDisclaimer && (
              <GridAreaDisclaimers>
                <Disclaimer
                  reference={resultDisclaimer.reference}
                  text={resultDisclaimer.text}
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- this is guaranteed to exist for module references. We decided against a complex type that would prevent the need of the assertion.
                  id={resultDisclaimer.disclaimerTextId!}
                />
              </GridAreaDisclaimers>
            )}
          </SimulatorContentLayout>
        </SimulatorLayout>

        {!tenantConfiguration.featureFlags?.hideFaq && (
          <FaqSection translations={translations.faqSection} />
        )}
      </AppLayout>
    </FaqListContextProvider>
  );
}

const AppLayout = styled.div`
  > * + * {
    margin-block-start: ${(props) =>
      props.theme.brand.spacing.appLayout?.blockGap ?? '0'};
  }
`;

const SimulatorContentLayout = styled.div<{isContentLayer?: boolean}>`
  display: grid;
  grid-template:
    'carsel'
    'inputs'
    'output'
    'configure'
    'resultdisclaimer' / 24fr;
  align-items: stretch;
  justify-items: stretch;
  max-width: 2560px;
  padding-inline-start: ${(props) =>
    props.theme.brand.spacing.oneColumnSimulatorContentLayout?.inlineStart ??
    '0'};
  padding-inline-end: ${(props) =>
    props.theme.brand.spacing.oneColumnSimulatorContentLayout?.inlineEnd ??
    '0'};

  @media (min-width: ${(props) =>
      props.theme.brand.spacing.customOneColumnBreakpoint ||
      props.theme.internal.Breakpoints.b960}px) {
    grid-template:
      ' carsel inputs '
      ' output inputs '
      ' . inputs '
      ' configure configure '
      ' resultdisclaimer . ';
    grid-template-columns: ${(props) =>
        props.theme.brand.spacing.twoColumnSimulatorContentLayout
          ?.columnWidths?.[0] ?? '1fr'} ${(props) =>
        props.theme.brand.spacing.twoColumnSimulatorContentLayout
          ?.columnWidths?.[1] ?? '1fr'};
    padding-inline-start: ${(props) =>
      props.isContentLayer
        ? '0'
        : props.theme.brand.spacing.twoColumnSimulatorContentLayout
            ?.inlineStart ?? '0'};
    padding-inline-end: ${(props) =>
      props.isContentLayer
        ? '0'
        : props.theme.brand.spacing.twoColumnSimulatorContentLayout
            ?.inlineEnd ?? '0'};
    column-gap: ${(props) =>
      props.theme.brand.spacing.twoColumnSimulatorContentLayout?.inlineGap ??
      '0'};
  }
`;

const GridAreaCarSelection = styled.div`
  grid-area: carsel;
  padding-block-start: ${(props) =>
    props.theme.brand.spacing.carSelectionSectionLayout?.blockStart ?? '0'};
  padding-block-end: ${(props) =>
    props.theme.brand.spacing.carSelectionSectionLayout?.blockEnd ?? '0'};
  padding-inline-start: ${(props) =>
    props.theme.brand.spacing.carSelectionSectionLayout?.inlineStart ?? '0'};
  padding-inline-end: ${(props) =>
    props.theme.brand.spacing.carSelectionSectionLayout?.inlineEnd ?? '0'};

  @media (min-width: ${(props) =>
      props.theme.brand.spacing.customOneColumnBreakpoint ||
      props.theme.internal.Breakpoints.b960}px) {
    padding-inline-start: ${(props) =>
      props.theme.brand.spacing.twoColumnSimulatorContentLayout?.inlineStart ??
      '0'};
    padding-inline-start: ${(props) =>
      props.theme.brand.spacing.carSelectionSectionLayout?.inlineStart ?? '0'};
  }
`;
